import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Lock, Unlock, MapPin, Pencil, Settings, Clock } from 'lucide-react';
import { formatDateRange } from '@/utils/dateFormat';
import { EventImage } from '@/components/common/EventImage';
import type { Organization } from '@/types/events';

interface EventCardProps {
  event: Organization;
  onPress: () => void;
  variant?: 'default' | 'compact' | 'involved';
  showEditButton?: boolean;
  onEdit?: (e: React.MouseEvent) => void;
  onManage?: (e: React.MouseEvent) => void;
  subscriptionBlocked?: boolean;
  /** only for variant="involved" */
  daysLeft?: number | null;
  daysUntilStart?: number | null;
  isEventStarted?: boolean;
  /** only for variant="involved" — renders a Leave action beside "View Event" */
  onLeave?: () => void;
  /** only for variant="involved" — opens the event scrolled to its shifts */
  onShifts?: () => void;
}

export function EventCard({
  event,
  onPress,
  variant = 'default',
  showEditButton = false,
  onEdit,
  onManage,
  subscriptionBlocked = false,
  daysLeft = null,
  daysUntilStart = null,
  isEventStarted = false,
  onLeave,
  onShifts,
}: EventCardProps) {
  const dateLabel =
    event.status === 'Scheduled'
      ? formatDateRange(event.start_date ?? null, event.end_date ?? null)
      : event.status ?? 'Active';

  // ── involved variant ──────────────────────────────────────────────────────
  if (variant === 'involved') {
    const timeLabel = isEventStarted
      ? daysLeft !== null
        ? daysLeft <= 0
          ? 'Ended'
          : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`
        : null
      : daysUntilStart !== null
        ? daysUntilStart <= 0
          ? 'Starts today'
          : `Starts in ${daysUntilStart} ${daysUntilStart === 1 ? 'day' : 'days'}`
        : null;

    return (
      <Box
        as="article"
        position="relative"
        w="full"
        borderRadius="24px"
        overflow="hidden"
        bg="white"
        border="1px solid"
        borderColor="volyx.mediumGray"
        boxShadow="0 2px 12px rgba(0,0,0,0.08)"
        transition="all 0.25s"
        _hover={{ boxShadow: '0 8px 28px rgba(0,0,0,0.13)', transform: 'translateY(-3px)' }}
        cursor="pointer"
        onClick={onPress}
      >
        {/* Image */}
        <Box position="relative" w="full" h="160px" overflow="hidden" bg="volyx.lightGray">
          <EventImage
            stored={event.image}
            w="full"
            h="full"
            objectFit="cover"
            transition="transform 0.3s"
            _hover={{ transform: 'scale(1.04)' }}
          />
          {/* Date badge (top-left) */}
          {dateLabel && (
            <Box
              position="absolute"
              left="3"
              top="3"
              bg="volyx.primary"
              color="white"
              fontSize="xs"
              fontWeight="semibold"
              px="3"
              py="1.5"
              borderRadius="16px"
              zIndex={1}
            >
              {dateLabel}
            </Box>
          )}
          {/* Time-left badge */}
          {timeLabel && (
            <Box
              position="absolute"
              right="3"
              top="3"
              bg="white"
              color="volyx.text"
              fontSize="xs"
              fontWeight="semibold"
              px="3"
              py="1.5"
              borderRadius="16px"
              zIndex={1}
              boxShadow="0 1px 4px rgba(0,0,0,0.10)"
              display="flex"
              alignItems="center"
              gap="1"
            >
              <Box as={Clock} boxSize="12px" color="volyx.textSecondary" />
              {timeLabel}
            </Box>
          )}
        </Box>

        {/* Body */}
        <Flex direction="column" gap="2.5" p="4">
          <Text
            as="h3"
            m="0"
            fontSize="md"
            fontWeight="bold"
            color="volyx.text"
            lineHeight="tight"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {event.name}
          </Text>

          {/* Wraps instead of squeezing: two cards side by side on a small screen are too narrow
              for three buttons, and View Event should not be the one that shrinks. */}
          <Flex gap="2" mt="1" flexWrap="wrap">
            <chakra.button
              flex="1 0 auto"
              py="2.5"
              px="4"
              borderRadius="16px"
              fontSize="sm"
              fontWeight="semibold"
              cursor="pointer"
              border="none"
              bg="volyx.action"
              color="white"
              _hover={{ bg: '#0063CC' }}
              transition="color 0.15s, background-color 0.15s"
              boxShadow="0 2px 8px rgba(0,0,0,0.15)"
              _focus={{ outline: 'none' }}
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); onPress(); }}
            >
              View Event
            </chakra.button>
            {onShifts && (
              <chakra.button
                py="2.5"
                px="4"
                borderRadius="16px"
                fontSize="sm"
                fontWeight="semibold"
                cursor="pointer"
                bg="transparent"
                color="volyx.action"
                border="1px solid"
                borderColor="volyx.action"
                _hover={{ bg: '#EEF5FF' }}
                transition="color 0.15s, background-color 0.15s"
                _focus={{ outline: 'none' }}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onShifts(); }}
              >
                Shifts
              </chakra.button>
            )}
            {onLeave && (
              <chakra.button
                py="2.5"
                px="4"
                borderRadius="16px"
                fontSize="sm"
                fontWeight="semibold"
                cursor="pointer"
                bg="transparent"
                color="volyx.error"
                border="1px solid"
                borderColor="volyx.error"
                _hover={{ bg: '#FFF5F5' }}
                transition="color 0.15s, background-color 0.15s"
                _focus={{ outline: 'none' }}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onLeave(); }}
              >
                Leave
              </chakra.button>
            )}
          </Flex>
        </Flex>
      </Box>
    );
  }
  // ── end involved variant ──────────────────────────────────────────────────

  return (
    <Box
      as="article"
      position="relative"
      w="full"
      borderRadius="24px"
      overflow="hidden"
      bg="white"
      border="1px solid"
      borderColor="volyx.mediumGray"
      boxShadow="0 2px 8px rgba(0,0,0,0.08)"
      transition="all 0.3s"
      _hover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'scale(1.02) translateY(-4px)' }}
      cursor="pointer"
      maxW={variant === 'compact' ? '200px' : { base: 'full', sm: '320px' }}
      flexShrink={variant === 'compact' ? 0 : undefined}
      role="button"
      tabIndex={0}
      onClick={onPress}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPress();
        }
      }}
    >
      <Box
        w="full"
        p="0"
        display="block"
      >
        <Box
          position="relative"
          w="full"
          style={{ aspectRatio: '16 / 10' }}
          display="block"
          bg="volyx.lightGray"
          overflow="hidden"
        >
          <EventImage
            stored={event.image}
            w="full"
            h="full"
            objectFit="cover"
            transition="transform 0.3s"
            _groupHover={{ transform: 'scale(1.1)' }}
          />
        </Box>
      </Box>
      <Box
        position="absolute"
        left="3"
        top="3"
        bg="volyx.primary"
        color="white"
        fontSize="xs"
        fontWeight="semibold"
        px="3"
        py="1.5"
      borderRadius="16px"
        fontFamily="body"
        zIndex={1}
      >
        {dateLabel}
      </Box>
      <Box
        position="absolute"
        right="3"
        top="3"
        bg="white"
        color="volyx.text"
        fontSize="sm"
        fontWeight="bold"
        px="3"
        py="1"
        borderRadius="24px"
        fontFamily="body"
        zIndex={1}
        boxShadow="0 1px 3px rgba(0,0,0,0.08)"
      >
        {event.volunteerCount ?? 0}/{event.slots ?? 0}
      </Box>
      <Flex direction="column" gap="3" p="4">
        <Flex flex="1" align="start" gap="3">
          <Box flex="1" minW="0">
            <Flex align="center" gap="1.5" mb="1.5" flexWrap={{ base: 'wrap' }} css={{ '@media (max-width: 375px)': { gap: '8px', marginBottom: '4px' } }}>
              <Text
                as="h3"
                m="0"
                fontSize="md"
                fontWeight="semibold"
                color="volyx.text"
                fontFamily="body"
                lineHeight="tight"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {event.name}
              </Text>
              {event.is_public !== false ? (
                <Box as={Unlock} boxSize="16px" color="volyx.success" flexShrink={0} aria-hidden />
              ) : (
                <Box as={Lock} boxSize="16px" color="volyx.error" flexShrink={0} aria-hidden />
              )}
            </Flex>
            <Flex align="center" gap="1.5" mb="2" css={{ '@media (max-width: 375px)': { marginBottom: 0 } }}>
              <Box as={MapPin} boxSize="16px" color="volyx.textSecondary" flexShrink={0} />
              <Text
                as="span"
                fontSize="sm"
                color="volyx.textSecondary"
                fontFamily="body"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {event.location}
              </Text>
            </Flex>
          </Box>
          {showEditButton && onEdit && (
            <chakra.button
              w="10"
              p="0"
              borderRadius="16px"
              bg="volyx.lightGray"
              color="volyx.text"
              border="none"
              cursor="pointer"
              display={{ base: 'flex', sm: 'none' }}
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              alignSelf="stretch"
              transition="opacity 0.2s"
              _hover={{ opacity: 0.9 }}
              opacity={subscriptionBlocked ? 0.5 : 1}
              css={subscriptionBlocked ? { cursor: 'not-allowed' } : undefined}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onEdit(e);
              }}
              disabled={subscriptionBlocked}
              aria-label="Edit event"
            >
              <Pencil size={16} />
            </chakra.button>
          )}
        </Flex>
        {showEditButton && onEdit && (
          <Flex gap="2" display={{ base: 'none', sm: 'flex' }}>
            <chakra.button
              flex="1"
              py="2.5"
              px="4"
              borderRadius="16px"
              fontSize="sm"
              fontWeight="semibold"
              fontFamily="body"
              cursor={subscriptionBlocked ? 'not-allowed' : 'pointer'}
              border="none"
              transition="opacity 0.2s"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="1.5"
              bg="volyx.lightGray"
              color="volyx.text"
              opacity={subscriptionBlocked ? 0.5 : 1}
              _hover={subscriptionBlocked ? {} : { opacity: 0.9, bg: 'volyx.mediumGray' }}
              _focus={{ outline: 'none', boxShadow: '0 0 0 2px #1F2932' }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onEdit(e);
              }}
              disabled={subscriptionBlocked}
            >
              <Box as="span" display="inline">Edit</Box>
              <Box as={Pencil} boxSize="16px" display="none" />
            </chakra.button>
            {onManage && (
              <chakra.button
                flex="1"
                py="2.5"
                px="4"
                borderRadius="16px"
                fontSize="sm"
                fontWeight="semibold"
                fontFamily="body"
                cursor={subscriptionBlocked ? 'not-allowed' : 'pointer'}
                border="none"
                transition="opacity 0.2s"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="1.5"
                bg="volyx.lightGray"
                color="volyx.text"
                opacity={subscriptionBlocked ? 0.5 : 1}
                _hover={subscriptionBlocked ? {} : { opacity: 0.9, bg: 'volyx.mediumGray' }}
                _focus={{ outline: 'none', boxShadow: '0 0 0 2px #1F2932' }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onManage(e);
                }}
                disabled={subscriptionBlocked}
              >
                <Box as="span" display="inline">Manage</Box>
                <Box as={Settings} boxSize="16px" display="none" />
              </chakra.button>
            )}
          </Flex>
        )}
        {!showEditButton && (
          <chakra.button
            w="full"
            py="2.5"
            px="4"
            borderRadius="16px"
            fontSize="sm"
            fontWeight="semibold"
            fontFamily="body"
            cursor="pointer"
            border="none"
            transition="opacity 0.2s"
            bg="volyx.primary"
            color="white"
            _hover={{ opacity: 0.9 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="1.5"
            _focus={{ outline: 'none', boxShadow: '0 0 0 2px #1F2932' }}
            onClick={onPress}
          >
            View
          </chakra.button>
        )}
      </Flex>
    </Box>
  );
}
